import { Button, Input, Select, TableProps } from "antd";
import moment from "moment";
import { useParams } from "react-router-dom";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import StatusItem from "../../../../components/common/StatusItem";
import CustomTable from "../../../../components/ui/CustomTable";
import { API_URL } from "../../../../constants/apiUrl";
import { AUTH_MANAGE_API } from "../../../../constants/constants";
import {
  AllSlabHistoryParams,
  AllSlabHistoryResponse,
} from "../../../../features/organization/contractual/JobDetails/jobDetails.type";
import { useGetAllSlabHistoryQuery } from "../../../../features/organization/contractual/JobDetails/jobDetailsApi";
import useDownloadEmployeeXlsx from "../../../../hooks/useDownloadExcelWithApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import { formatDate } from "../../../../lib/time";
import {
  addCommaInNumber,
  countParPageSerialNumber,
} from "../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
} from "../../../../utils/utils";
import ExportIcon from "../_icons/ExportIcon";
import ManageAssignSlab from "./ManageAssignSlab";

export const STATUSES = [
  {
    label: "Upcoming",
    value: "upcoming",
    className: "status-warning rounded-lg",
  },
  // {
  //   label: "Ongoing",
  //   value: "ongoing",
  //   className: "status-warning rounded-lg",
  // },
  {
    label: "Replaced",
    value: "replaced",
    className: "status-danger rounded-lg",
  },
  {
    label: "Active",
    value: "active",
    className: "status-success rounded-lg",
  },
  // {
  //   label: "Completed",
  //   value: "completed",
  //   className: "status-success rounded-lg",
  // },
];

const SlabHistory = () => {
  const { id } = useParams();
  const { loading: excelLoading, downloadXlsx } = useDownloadEmployeeXlsx();
  const { open, handleOpen, handleClose } = useModal();
  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleSearch,
    handleFilter,
    handlePagination,
  } = useTable<AllSlabHistoryParams>();

  const { data, isLoading, isFetching, isError, error } =
    useGetAllSlabHistoryQuery({
      arg: {
        params: {
          ...filter,
          search: keyword,
        },
      },
      id: Number(id),
    });

  useError(isError, error, "Failed to fetch slab history data");

  const columns: TableProps<AllSlabHistoryResponse>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      align: "center",
      title: "Period",
      dataIndex: "period_from",
      render: (val) => formatDate(val),
    },
    {
      title: "Salary Slab",
      dataIndex: "salary_slab",
      render: (val) => {
        return valueOrNa(val?.slab_name);
      },
    },
    {
      align: "center",
      title: "Effective Date",
      dataIndex: "effective_date",
      render: (val) => formatDate(val),
    },
    {
      align: "center",
      title: "Base Salary",
      dataIndex: "salary_slab",
      render: (val) => addCommaInNumber(val?.fixed_amount || 0),
    },
    {
      align: "center",
      title: "Variable Tier Earned",
      dataIndex: "salary_slab",
      render: (val) => addCommaInNumber(val?.variable_amount || 0),
    },
    {
      align: "center",
      title: "Status",
      dataIndex: "computed_status",
      render: (val) => <StatusItem value={val} list={STATUSES} />,
    },
  ];

  // ---------- Excel Download Function   ------------
  const handleDownload = () => {
    downloadXlsx(
      `${AUTH_MANAGE_API + API_URL.getAllSlabHistory}/${id}`,
      {
        ...filter,
        page: page,
        search: keyword,
        skip: 0,
        limit: 1000000000000000,
      },
      `Slab_History_${moment().format("DD MMMM YYYY")}_${moment().format(
        "h.mm.ss A"
      )}`,
      "assignments",
      (item: AllSlabHistoryResponse["assignments"][0]) => {
        return {
          Period: formatDate(item?.period_from) || "-",
          "Salary Slab": item?.salary_slab?.slab_name || "-",
          "Effective Date": formatDate(item?.effective_date) || "-",
          "Base Salary":
            item?.salary_slab?.fixed_amount !== undefined &&
            item?.salary_slab?.fixed_amount !== null
              ? addCommaInNumber(item?.salary_slab?.fixed_amount)
              : "-",
          "Variable Tier Earned":
            item?.salary_slab?.variable_amount !== undefined &&
            item?.salary_slab?.variable_amount !== null
              ? addCommaInNumber(item?.salary_slab?.variable_amount)
              : "-",
          Status: capitalize(item?.computed_status) || "-",
        };
      }
    );
  };

  return (
    <>
      <CustomTable
        loading={isLoading || isFetching}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data.total}
        dataSource={addKeyInArray(data?.data.assignments)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />

            <Select
              allowClear
              showSearch
              placeholder="Status"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              filterOption={selectSearchOption}
              options={selectArrayOption(STATUSES, "label", "value")}
              onChange={(value) => handleFilter("status", value)}
            />

            <Button
              type="default"
              className="!bg-purple-50 !border-fuchsia-600 !text-fuchsia-600"
              onClick={handleDownload}
              loading={excelLoading}
            >
              <ExportIcon /> Export
            </Button>

            <Button
              type="primary"
              color="primary"
              onClick={() => handleOpen({ type: "ADD" })}
            >
              Assign Another Slab
            </Button>
          </>
        }
      />

      <ManageAssignSlab
        type={open.type}
        handleClose={handleClose}
        open={open.open && open.type === "ADD"}
      />
    </>
  );
};

export default SlabHistory;
